import { authAPI } from '../api/authAPI';
import { formatUserFacingError } from './formatUserFacingError';
import { trackOtpSendFailed, trackOtpSendSucceeded } from './analytics';

const MAX_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [0, 1000, 2500];
const inFlight = new Map();

export function isRetryableAuthNetworkError(error) {
  if (!error || error.response) {
    return false;
  }
  const code = String(error.code || '');
  const message = String(error.message || '');
  return (
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    code === 'ERR_CANCELED' ||
    /network error/i.test(message) ||
    /timeout/i.test(message)
  );
}

function flightKey(phone, purpose) {
  return `${purpose}:${phone}`;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function errorCode(error) {
  if (error?.response?.status) {
    return `http_${error.response.status}`;
  }
  if (error?.code) {
    return String(error.code);
  }
  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message.slice(0, 80);
  }
  return 'unknown';
}

async function runSend(phone, purpose, options = {}) {
  let lastError;
  const preferSms = Boolean(options.preferSms);
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    const delay = RETRY_DELAYS_MS[attempt - 1] || 0;
    if (delay) {
      await sleep(delay);
    }
    try {
      const data = await authAPI.sendOTP(phone, purpose, { preferSms });
      trackOtpSendSucceeded({
        purpose,
        attempt,
        delivery_channel: data?.delivery_channel || null,
        fallback_used: Boolean(data?.fallback_used),
      });
      return data;
    } catch (error) {
      lastError = error;
      const retryable = isRetryableAuthNetworkError(error) && attempt < MAX_ATTEMPTS;
      trackOtpSendFailed({
        purpose,
        attempt,
        error_code: errorCode(error),
        http_status: error?.response?.status ?? null,
        will_retry: retryable,
      });
      if (!retryable) {
        break;
      }
    }
  }
  const message = formatUserFacingError(
    lastError,
    "Couldn't send the verification code. Stay on this page and try again.",
  );
  if (lastError && typeof lastError === 'object') {
    lastError.userMessage = message;
    throw lastError;
  }
  throw Object.assign(new Error(message), { userMessage: message });
}

export function sendOtpReliable(phone, purpose, options = {}) {
  const key = flightKey(phone, purpose);
  const existing = inFlight.get(key);
  if (existing) {
    return existing;
  }
  const promise = runSend(phone, purpose, options).finally(() => {
    if (inFlight.get(key) === promise) {
      inFlight.delete(key);
    }
  });
  inFlight.set(key, promise);
  return promise;
}
